import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { contactService } from '../../../services/contactService';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('ContactsInput');

const categories = [
  'Family',
  'Friends', 
  'Medical',
  'Legal',
  'Property',
  'Business',
  'EOL',
  'Spiritual',
  'Pet',
  'Personal',
  'Other'
];

const relationshipTypes: { [key: string]: string[] } = {
  Family: ['Spouse', 'Parent', 'Child', 'Sibling', 'Grandparent', 'Grandchild', 'Other Family'],
  Friends: ['Best Friend', 'Close Friend', 'Neighbor', 'Colleague', 'Acquaintance'],
  Medical: ['Primary Care Doctor', 'Specialist', 'Dentist', 'Pharmacist', 'Therapist', 'Emergency Contact'],
  Legal: ['Attorney', 'Accountant', 'Financial Advisor', 'Insurance Agent', 'Notary'],
  Property: ['Landlord', 'Property Manager', 'Real Estate Agent', 'Contractor', 'Maintenance'],
  Business: ['Boss', 'Coworker', 'Client', 'Vendor', 'Business Partner', 'HR Representative'],
  EOL: ['Executor', 'Power of Attorney', 'Healthcare Proxy', 'Funeral Director', 'Clergy'],
  Spiritual: ['Pastor', 'Priest', 'Rabbi', 'Imam', 'Spiritual Advisor', 'Church Member'],
  Pet: ['Veterinarian', 'Pet Sitter', 'Dog Walker', 'Groomer', 'Pet Store', 'Animal Hospital'],
  Personal: ['Hairdresser', 'Personal Trainer', 'Babysitter', 'Tutor', 'Coach'],
  Other: ['Emergency Services', 'Utility Company', 'Government Office', 'Other']
};

const callTags = ['First Call', 'Second Call', 'Shared', 'Favorite'];

const states = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const ContactsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const [formData, setFormData] = useState({
    category: '',
    relationshipType: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Phone number formatting function
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Apply formatting based on length
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  // Email validation function
  const isValidEmail = (email: string) => {
    if (!email) return true; // Email is optional, so empty is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle route params (category pre-selection or contact editing)
  useEffect(() => {
    const params = route.params as any;
    
    // If editing existing contact
    if (params?.contact) {
      const contact = params.contact;
      setFormData({
        category: contact.category,
        relationshipType: contact.relationship,
        firstName: contact.firstName,
        lastName: contact.lastName,
        phoneNumber: contact.phoneNumber,
        email: contact.email || '',
        city: contact.city || '',
        state: contact.state || '',
        zip: contact.zip || '',
        notes: contact.notes || '',
      });
      setSelectedTags(contact.tags || []);
    }
    // If pre-selecting category for new contact
    else if (params?.category) {
      setFormData(prev => ({ ...prev, category: params.category }));
    }
  }, [route.params]);

  const handleSubmit = async () => {
    if (!formData.category || !formData.relationshipType || !formData.firstName || !formData.lastName || !formData.phoneNumber) {
      Alert.alert('Missing Information', 'Please fill in all required fields.');
      return;
    }

    // Validate email format if provided
    if (formData.email && !isValidEmail(formData.email)) {
      Alert.alert('Invalid Email', 'Please enter a valid email address or leave it blank.');
      return;
    }

    setIsSubmitting(true);
    try {
      const params = route.params as any;
      const isEditing = params?.contact;
      
      const contactData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        category: formData.category.toLowerCase(),
        relationship: formData.relationshipType,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        tags: selectedTags,
        notes: formData.notes,
      };
      
      let contactId: string;
      
      if (isEditing) {
        // Update existing contact
        logger.debug('Updating contact:', { ...contactData, email: contactData.email ? '[HIDDEN]' : undefined });
        const updatedContact = await contactService.updateContact(params.contact.id, contactData);
        contactId = updatedContact.id;
      } else {
        // Create new contact
        logger.debug('Creating contact:', { ...contactData, email: contactData.email ? '[HIDDEN]' : undefined });
        contactId = await contactService.storeContact(contactData);
      }

      Alert.alert(
        'Success',
        `Contact ${isEditing ? 'updated' : 'saved'} successfully!`,
        [
          {
            text: 'OK',
            onPress: () => {
              if (isEditing) {
                // For editing, go back to the data screen with updated contact
                const updatedContactForDisplay = {
                  id: params.contact.id,
                  ...contactData,
                  tags: selectedTags,
                };
                navigation.navigate('ContactsDataScreen' as any, { contact: updatedContactForDisplay });
              } else {
                // For new contact, go to dashboard
                navigation.navigate('ContactsDashboardScreen' as any);
              }
            }
          }
        ]
      );
    } catch (error: any) {
      logger.error('Error saving contact:', error);
      let errorMessage = 'Failed to save contact. Please try again.';
      
      if (error.message) {
        if (error.message.includes('Network Error') || error.message.includes('network')) {
          errorMessage = 'Network error. Contact saved locally and will sync when connection is restored.';
        } else if (error.message.includes('Server error')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    setFormData({ ...formData, category, relationshipType: '' }); // Reset relationship type when category changes
    setShowCategoryModal(false);
  };

  const handleRelationshipSelect = (relationship: string) => {
    setFormData({ ...formData, relationshipType: relationship });
    setShowRelationshipModal(false);
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, state });
    setShowStateModal(false);
  };

  const handleTagPress = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const availableRelationships = formData.category ? relationshipTypes[formData.category] : [];

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false} automaticallyAdjustKeyboardInsets={true}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>
            {(route.params as any)?.contact ? 'Edit' : 'Add'}
          </Text>
          <Text style={styles.allContactsText}>Contact Details</Text>
        </View>

        <View style={styles.formContainer}>
          <View style={styles.card}>
            <TouchableOpacity 
              style={styles.dropdownInput}
              onPress={() => setShowCategoryModal(true)}
            >
              <Text style={[styles.dropdownText, formData.category && { color: '#1C2C4F' }]}>
                {formData.category || 'Select Category'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.dropdownInput, !formData.category && { opacity: 0.5 }]}
              onPress={() => formData.category && setShowRelationshipModal(true)}
              disabled={!formData.category}
            >
              <Text style={[styles.dropdownText, formData.relationshipType && { color: '#1C2C4F' }]}>
                {formData.relationshipType || 'Select Relationship Type'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>

            <TextInput
              style={styles.input}
              value={formData.firstName}
              onChangeText={(text) => setFormData({ ...formData, firstName: text })}
              placeholder="Enter first name"
              placeholderTextColor="#A9A9A9"
            />

            <TextInput
              style={styles.input}
              value={formData.lastName}
              onChangeText={(text) => setFormData({ ...formData, lastName: text })}
              placeholder="Enter last name"
              placeholderTextColor="#A9A9A9"
            />

            <TextInput
              style={styles.input}
                              value={formData.phoneNumber}
                onChangeText={(text) => setFormData({ ...formData, phoneNumber: formatPhoneNumber(text) })}
              placeholder="Enter phone number"
              keyboardType="phone-pad"
              placeholderTextColor="#A9A9A9"
            />

            <TextInput
              style={[styles.input, emailError && { borderColor: '#FF6B6B', borderWidth: 2 }]}
              value={formData.email}
              onChangeText={(text) => {
                setFormData({ ...formData, email: text });
                // Clear error when user starts typing
                if (emailError) setEmailError('');
              }}
              onBlur={() => {
                // Validate on blur if email is not empty
                if (formData.email && !isValidEmail(formData.email)) {
                  setEmailError('Invalid email format');
                } else {
                  setEmailError('');
                }
              }}
              placeholder="Enter email address (optional)"
              keyboardType="email-address"
              autoCapitalize="none"
              placeholderTextColor="#A9A9A9"
            />
            {emailError ? (
              <Text style={styles.errorText}>{emailError}</Text>
            ) : null}

            <TextInput
              style={styles.input}
              value={formData.city}
              onChangeText={(text) => setFormData({ ...formData, city: text })}
              placeholder="Enter city"
              placeholderTextColor="#A9A9A9"
            />

            <View style={styles.rowContainer}>
              <TouchableOpacity 
                style={[styles.input, styles.stateInput]}
                onPress={() => setShowStateModal(true)}
              >
                <Text style={[styles.dropdownText, formData.state && { color: '#1C2C4F' }]}>
                  {formData.state || 'State'}
                </Text>
                <Image
                  source={require('../../../assets/icons/down.png')}
                  style={styles.dropdownIcon}
                />
              </TouchableOpacity>

              <TextInput
                style={[styles.input, styles.zipInput]}
                placeholder="Zip"
                value={formData.zip}
                onChangeText={(text) => setFormData({ ...formData, zip: text })}
                keyboardType="numeric"
                placeholderTextColor="#A9A9A9"
              />
            </View>

            <TextInput
              style={[styles.input, styles.notesInput]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter notes (optional)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
            />

            <Text style={styles.tagsLabel}>Contact Tags</Text>
            <View style={styles.tagsContainer}>
              {callTags.map((tag) => (
                <TouchableOpacity
                  key={tag}
                  style={[
                    styles.tagButton,
                    selectedTags.includes(tag) && styles.tagButtonSelected,
                  ]}
                  onPress={() => handleTagPress(tag)}
                >
                  <Text style={[
                    styles.tagText,
                    selectedTags.includes(tag) && styles.tagTextSelected,
                  ]}>{tag}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <TouchableOpacity 
              style={[styles.submitButton, isSubmitting && { opacity: 0.7 }]} 
              onPress={handleSubmit}
              disabled={isSubmitting}
            >
              <Text style={styles.submitButtonText}>
                {isSubmitting ? 'Saving...' : 'Save Contact'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <Modal
        visible={showCategoryModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Category</Text>
            <FlatList
              data={categories}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => handleCategorySelect(item)}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowCategoryModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showRelationshipModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Relationship Type</Text>
            <FlatList
              data={availableRelationships}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => handleRelationshipSelect(item)}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowRelationshipModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showStateModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select State</Text>
            <FlatList
              data={states}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(item)}
                >
                  <Text style={styles.stateText}>{item}</Text>
                </TouchableOpacity>
              )}
              numColumns={4}
              columnWrapperStyle={styles.stateColumns}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowStateModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allContactsText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  formContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  input: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(30),
    height: s(50),
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  errorText: {
    color: '#FF6B6B',
    fontSize: ms(12, 0.3),
    marginTop: ms(-10),
    marginBottom: ms(10),
    marginLeft: ms(20),
  },
  notesInput: {
    height: s(80),
    paddingTop: ms(15),
    textAlignVertical: 'top',
  },
  dropdownInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F3F6F8',
    borderRadius: ms(30),
    height: s(50),
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  dropdownText: {
    color: '#A9A9A9',
    fontSize: ms(16, 0.3),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#A9A9A9',
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  stateInput: {
    flex: 1,
    marginRight: ms(10),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  zipInput: {
    width: '35%',
  },
  tagsLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(10),
    marginTop: ms(10),
    fontWeight: '500',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: ms(20),
  },
  tagButton: {
    paddingHorizontal: ms(15),
    paddingVertical: ms(8),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    marginRight: ms(10),
    marginBottom: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  tagButtonSelected: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  tagText: {
    color: '#6B7280',
    fontSize: ms(14, 0.3),
    fontWeight: '500',
  },
  tagTextSelected: {
    color: '#FFFFFF',
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    height: s(50),
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(20),
    width: '80%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: ms(15),
    textAlign: 'center',
  },
  categoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEEEEE',
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    marginRight: ms(12),
  },
  categoryText: {
    fontSize: ms(16, 0.3),
    color: '#333333',
  },
  closeButton: {
    marginTop: ms(15),
    padding: ms(15),
    alignItems: 'center',
    backgroundColor: '#F3F6F8',
    borderRadius: ms(25),
  },
  closeButtonText: {
    color: '#333333',
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
  stateItem: {
    flex: 1,
    alignItems: 'center',
    padding: ms(12),
    marginVertical: ms(4),
  },
  stateText: {
    fontSize: ms(16, 0.3),
    color: '#333333',
    fontWeight: '500',
  },
  stateColumns: {
    justifyContent: 'space-between',
    paddingHorizontal: ms(8),
  },
});
